import { TextField } from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { P, match } from "ts-pattern";

import { InputImageWithPreview } from "~/components/common/InpuImageWithPreview";
import { Question } from "~/components/database/detail/database-content/types";
import {
  AnswerFormProps,
  Coordinate,
} from "~/components/database/detail/response-modal-content/response-form/AnswerForm";
import { useGraphqlClientWithAuth } from "~/lib/client/hooks/useGraphqlClient";
import {
  readBlobAsDataUrl,
  useDataUrlFromBlob,
} from "~/lib/client/images/useDataFromBlob";
import { AnswerTypeEnum } from "~/lib/graphql/client";

type InputAreaProps = Omit<AnswerFormProps, "question"> & {
  question: Pick<Question, "id" | "answerType">;
};

const InputArea = (props: InputAreaProps) =>
  match(props.question.answerType)
    .with(
      P.union(
        AnswerTypeEnum.Text,
        AnswerTypeEnum.LongText,
        AnswerTypeEnum.ShortText,
        AnswerTypeEnum.ImageUrl,
        AnswerTypeEnum.Url
      ),
      () => <TextAnswerForm {...props} />
    )
    .with(AnswerTypeEnum.Number, () => <NumberAnswerForm {...props} />)
    .with(AnswerTypeEnum.Location, () => <LocationAnswerForm {...props} />)
    .with(AnswerTypeEnum.Date, () => <DateAnswerForm {...props} />)
    .with(AnswerTypeEnum.Reference, () => <div>入力できません</div>)
    .exhaustive();

/**
 * SHORT_TEXT, LONG_TEXT, IMAGE_URL, URL の回答フォームブロック
 * 画像のアップロードを行い、そのURLを回答として保存したりもする
 */
const TextAnswerForm = ({
  question,
  updateResponseAnswer,
  setIsValid,
  responseAnswerInput,
}: InputAreaProps) => {
  const [imageFile, setImageFile] = useState<Blob | null>(null);
  const imageData = useDataUrlFromBlob(imageFile);

  const client = useGraphqlClientWithAuth();
  const dispatchAnswer = useCallback(
    (value: string): void => {
      updateResponseAnswer({
        questionId: question.id,
        answerTypeEnum: question.answerType,
        textAnswer: { value },
      });
    },
    [question.id, question.answerType, updateResponseAnswer]
  );

  const isValid = useCallback(
    (nextValue: string) =>
      match(responseAnswerInput.answerTypeEnum)
        .with(
          P.union(
            AnswerTypeEnum.LongText,
            AnswerTypeEnum.ShortText,
            AnswerTypeEnum.Text
          ),
          () => nextValue !== ""
        )
        .with(P.union(AnswerTypeEnum.Url, AnswerTypeEnum.ImageUrl), () => {
          try {
            const url = new URL(nextValue);
            return url.protocol === "http:" || url.protocol === "https:";
          } catch {
            return false;
          }
        })
        .with(
          P.union(
            AnswerTypeEnum.Reference,
            AnswerTypeEnum.Date,
            AnswerTypeEnum.Location,
            AnswerTypeEnum.Number
          ),
          () => false
        )
        .exhaustive(),
    [responseAnswerInput.answerTypeEnum]
  );

  const updateAnswer = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    dispatchAnswer(event.target.value);
    setIsValid(isValid(event.target.value));
  };

  const isImageResponse =
    responseAnswerInput.answerTypeEnum === AnswerTypeEnum.ImageUrl;

  const handleOnChangeFileInput = useCallback(
    async (file: File | Blob | null) => {
      if (file === null) return;
      if (!client) return;

      setImageFile(file);
      const imageData = await readBlobAsDataUrl(file);
      const res = await client.uploadFileForResponse({
        input: {
          base64EncodedFile: imageData,
          fileName: file.name,
          public: true,
        },
      });
      const url = res.uploadFile?.url;

      if (!url) {
        alert("画像のアップロードに失敗しました");
        return;
      }

      dispatchAnswer(url);
      setIsValid(isValid(url));
    },
    [setImageFile, client, dispatchAnswer, isValid, setIsValid]
  );

  return (
    <>
      {isImageResponse && (
        <InputImageWithPreview
          imageData={imageData}
          onChange={handleOnChangeFileInput}
        />
      )}
      {!imageData && (
        <TextField
          type="text"
          value={responseAnswerInput.textAnswer?.value || ""}
          onChange={updateAnswer}
          multiline={
            responseAnswerInput.answerTypeEnum == AnswerTypeEnum.LongText
          }
          rows={
            responseAnswerInput.answerTypeEnum == AnswerTypeEnum.LongText
              ? 4
              : 1
          }
          fullWidth
          InputProps={{
            sx: {
              background: "#eee",
            },
          }}
        />
      )}
    </>
  );
};

const NumberAnswerForm = ({
  question,
  updateResponseAnswer,
  setIsValid,
  responseAnswerInput,
}: InputAreaProps) => {
  const dispatchAnswer = (value: number) => {
    updateResponseAnswer({
      questionId: question.id,
      answerTypeEnum: question.answerType,
      numberAnswer: { value },
    });
  };

  const updateAnswer = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    dispatchAnswer(Number(event.target.value));
    const isValid = !isNaN(Number(event.target.value));
    setIsValid(isValid);
  };

  useEffect(() => {
    setIsValid(!isNaN(responseAnswerInput.numberAnswer?.value || 0));
  }, [setIsValid, responseAnswerInput]);

  return (
    <TextField
      type={"number"}
      onChange={updateAnswer}
      value={responseAnswerInput.numberAnswer?.value || 0}
      fullWidth
      InputProps={{
        sx: {
          background: "#eee",
        },
      }}
      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
      placeholder="0"
    />
  );
};

const LocationAnswerForm = ({
  question,
  updateResponseAnswer,
  setIsValid,
  responseAnswerInput,
}: InputAreaProps) => {
  const dispatchAnswer = (value: Coordinate): void => {
    updateResponseAnswer({
      questionId: question.id,
      answerTypeEnum: question.answerType,
      locationAnswer: value,
    });
  };

  const isValidCoordinate = useCallback(() => {
    const answer = responseAnswerInput.locationAnswer;
    if (!answer) {
      return false;
    }

    return (
      -90 <= answer.latitude &&
      answer.latitude <= 90 &&
      -180 <= answer.longitude &&
      answer.longitude <= 180
    );
  }, [responseAnswerInput]);

  const updateAnswer =
    (key: "latitude" | "longitude") =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const eventValue = Number(event.target.value);
      const answer = responseAnswerInput.locationAnswer;
      if (!answer) {
        const defaultValue = { latitude: 0, longitude: 0 };
        dispatchAnswer({ ...defaultValue, [key]: eventValue });
      } else {
        dispatchAnswer({ ...answer, [key]: eventValue });
      }

      setIsValid(isValidCoordinate());
    };

  const answer = responseAnswerInput.locationAnswer;

  return (
    <>
      <div>
        <label>緯度</label>
        <TextField
          onChange={updateAnswer("latitude")}
          type="number"
          value={answer?.latitude || 0}
          fullWidth
          InputProps={{
            sx: {
              background: "#eee",
            },
          }}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          placeholder="0"
        />
      </div>
      <div>
        <label>経度</label>
        <TextField
          onChange={updateAnswer("longitude")}
          type="number"
          value={answer?.longitude || 0}
          fullWidth
          InputProps={{
            sx: {
              background: "#eee",
            },
          }}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          placeholder="0"
        />
      </div>
      {!isValidCoordinate && (
        <p>緯度は-90から90の間、軽度は-180から180の間で入力してください</p>
      )}
    </>
  );
};

const DateAnswerForm = ({
  question,
  updateResponseAnswer,
  setIsValid,
  responseAnswerInput,
}: InputAreaProps) => {
  const answer = responseAnswerInput.dateAnswer;

  const updateAnswer = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateResponseAnswer({
      questionId: question.id,
      answerTypeEnum: question.answerType,
      dateAnswer: { value: event.target.value },
    });

    setIsValid(event.target.value !== "");
  };

  return (
    <TextField
      type={"date"}
      value={answer?.value || ""}
      onChange={updateAnswer}
    />
  );
};

export default InputArea;
